import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  return config;
});

export const collaborationApi = {
  /**
   * CREATE COLLABORATION POST
   * Endpoint: POST /api/posts/collaboration
   * Body: { postType, content, collaboration: {...}, visibility }
   * Response: { success, message, post }
   */
  createCollaboration: async (postData) => {
    return apiClient.post("/posts/collaboration", postData);
  },

  /**
   * APPLY TO COLLABORATION
   * Endpoint: POST /api/posts/:id/apply
   * Body: { motivation, positionIndex }
   * Response: { success, message, application }
   */
  applyToCollaboration: async (postId, applicationData) => {
    return apiClient.post(`/posts/${postId}/apply`, applicationData);
  },

  /**
   * REVIEW APPLICATION (Accept/Reject)
   * Endpoint: POST /api/posts/:postId/applications/:applicationId/review
   * Body: { decision: "accepted" | "rejected", reviewNote }
   * Response: { success, message, application, addedToRoom: true }
   */
  reviewApplication: async (postId, applicationId, decision, reviewNote) => {
    return apiClient.post(
      `/posts/${postId}/applications/${applicationId}/review`,
      { decision, reviewNote }
    );
  },

  /**
   * GET OPPORTUNITIES (Browse)
   * Endpoint: GET /api/posts/collaboration/opportunities?limit=20&offset=0&skills=Python,ML
   * Response: { success, opportunities: [...posts], count }
   */
  getOpportunities: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.offset) queryParams.append("offset", params.offset);
    if (params.skills && params.skills.length > 0) {
      queryParams.append("skills", params.skills.join(","));
    }
    return apiClient.get(`/posts/collaboration/opportunities?${queryParams}`);
  },

  /**
   * GET POST APPLICATIONS (Author Only)
   * Endpoint: GET /api/posts/:id/applications
   * Response: { success, data: { applications, totalApplications, pendingCount, acceptedCount, rejectedCount } }
   */
  getPostApplications: async (postId) => {
    return apiClient.get(`/posts/${postId}/applications`);
  },

  /**
   * GET MY APPLICATIONS
   * Endpoint: GET /api/posts/collaboration/my-applications?status=pending&limit=20
   * Response: { success, data: { applications, total } }
   */
  getMyApplications: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.limit) queryParams.append("limit", params.limit);
    return apiClient.get(`/posts/collaboration/my-applications?${queryParams}`);
  },

  /**
   * WITHDRAW APPLICATION
   * Endpoint: DELETE /api/posts/:postId/applications/:applicationId/withdraw
   * Response: { success, message }
   */
  withdrawApplication: async (postId, applicationId) => {
    return apiClient.delete(
      `/posts/${postId}/applications/${applicationId}/withdraw`
    );
  },

  /**
   * CLOSE COLLABORATION
   * Endpoint: POST /api/posts/:id/close-collaboration
   * Response: { success, message, post }
   */
  closeCollaboration: async (postId) => {
    return apiClient.post(`/posts/${postId}/close-collaboration`);
  },

  /**
   * GET TRENDING COLLABORATIONS
   * Endpoint: GET /api/posts/collaboration/trending?limit=10
   * Response: { success, collaborations: [...], count }
   */
  getTrendingCollaborations: async (limit = 10) => {
    return apiClient.get(`/posts/collaboration/trending?limit=${limit}`);
  },
};

export default collaborationApi;
